import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useToast } from '@/hooks/use-toast'
import { signIn, resetPassword } from '@/lib/auth'
import { useAuth } from '@/contexts/AuthContext'
import { Modal, ModalHeader, ModalTitle } from '@/components/ui/modal'

export function Login() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [showResetModal, setShowResetModal] = useState(false)
  const [resetEmail, setResetEmail] = useState('')
  const [resetLoading, setResetLoading] = useState(false)
  const navigate = useNavigate()
  const { toast } = useToast()
  const { user } = useAuth()

  useEffect(() => {
    if (user) {
      navigate('/dashboard')
    }
  }, [user, navigate])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    try {
      const { error } = await signIn(email, password)

      if (error) {
        toast({
          variant: "destructive",
          title: "Falha na autenticação",
          description: error.message,
        })
        return
      }
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: error.message || "Ocorreu um erro inesperado",
      })
    } finally {
      setLoading(false)
    }
  }

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    setResetLoading(true)

    try {
      const { error } = await resetPassword(resetEmail)

      if (error) {
        toast({
          variant: "destructive",
          title: "Erro",
          description: error.message,
        })
        return
      }

      toast({
        title: "Email enviado",
        description: "Verifique sua caixa de entrada para redefinir sua senha",
      })
      setShowResetModal(false)
      setResetEmail('')
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: error.message || "Ocorreu um erro inesperado",
      })
    } finally {
      setResetLoading(false)
    }
  }

  if (user) {
    return null
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 py-8 bg-background">
      <div className="w-full max-w-[380px] space-y-6">
        <div className="text-center">
          <div className="flex flex-col items-center gap-3">
            <img src="/logo-serur.png" alt="Serur" className="h-10 w-auto" />
            <img src="/logo-auth-orange.png" alt="Auth" className="h-9 w-auto" />
          </div>
          <p className="mt-4 text-sm text-muted-foreground">
            Sistema de autenticação de dois fatores
          </p>
        </div>

        <form onSubmit={handleSubmit} className="bg-card border border-border rounded-lg p-4 sm:p-6 space-y-4 sm:space-y-6">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-foreground">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="Digite seu email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="bg-secondary border-border text-foreground placeholder:text-muted-foreground h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-foreground">Senha</Label>
              <Input
                id="password"
                type="password"
                placeholder="Digite sua senha"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="bg-secondary border-border text-foreground placeholder:text-muted-foreground h-11"
              />
            </div>
          </div>

          <div className="flex flex-col space-y-4">
            <Button
              type="submit"
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90 h-11 text-base font-medium"
              disabled={loading}
            >
              {loading ? "Entrando..." : "Entrar"}
            </Button>

            <button
              type="button"
              onClick={() => setShowResetModal(true)}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              Esqueceu sua senha?
            </button>
          </div>
          <div className='flex justify-center  text-muted-foreground text-sm'>
            <p>Não tem uma conta? <Link to="/register" className='hover:text-foreground hover:bg-accent cursor-pointer'>Cadastre-se </Link></p>
          </div>
        </form>
      </div>

      <Modal isOpen={showResetModal} onClose={() => setShowResetModal(false)}>
        <ModalHeader>
          <ModalTitle>Recuperar Senha</ModalTitle>
        </ModalHeader>

        <form onSubmit={handleResetPassword} className="mt-4 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="reset-email" className="text-foreground">Email</Label>
            <Input
              id="reset-email"
              type="email"
              placeholder="Digite seu email"
              value={resetEmail}
              onChange={(e) => setResetEmail(e.target.value)}
              required
              className="bg-secondary border-border text-foreground placeholder:text-muted-foreground"
            />
          </div>

          <p className="text-sm text-muted-foreground">
            Enviaremos um link para você redefinir sua senha.
          </p>

          <div className="flex space-x-2">
            <Button
              type="button"
              variant="outline"
              className="flex-1 border-border text-muted-foreground hover:text-foreground"
              onClick={() => setShowResetModal(false)}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90"
              disabled={resetLoading}
            >
              {resetLoading ? "Enviando..." : "Enviar Link"}
            </Button>
          </div>
        </form>
      </Modal>

    </div>
  )
}